import React from 'react';
import { Alert, Button } from '../ui';
import { AlertTriangle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

interface TrialBannerProps {
  trialEndsAt?: string;
  trialExpired?: boolean;
  plan: 'free' | 'pro' | 'enterprise';
}

export const TrialBanner: React.FC<TrialBannerProps> = ({
  trialEndsAt,
  trialExpired,
  plan
}) => {
  // Only show for free plan
  if (plan !== 'free') {
    return null;
  }

  const getDaysRemaining = () => {
    if (!trialEndsAt) return 0;
    const now = new Date();
    const end = new Date(trialEndsAt);
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining();

  if (trialExpired) {
    return (
      <Alert variant="danger">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} />
            <div>
              <p className="font-semibold">Your trial has expired</p>
              <p className="text-sm mt-1">
                Upgrade to a paid plan to continue using MCP Hub Universal.
              </p>
            </div>
          </div>
          <Link to="/settings">
            <Button variant="danger" size="sm">
              Upgrade Now
            </Button>
          </Link>
        </div>
      </Alert>
    );
  }

  if (daysRemaining <= 3) {
    return (
      <Alert variant="warning">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock size={20} />
            <div>
              <p className="font-semibold">
                {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in your trial
              </p>
              <p className="text-sm mt-1">
                Upgrade to Pro to unlock unlimited access and advanced features.
              </p>
            </div>
          </div>
          <Link to="/settings">
            <Button variant="primary" size="sm">
              Upgrade
            </Button>
          </Link>
        </div>
      </Alert>
    );
  }

  if (daysRemaining <= 7) {
    return (
      <Alert variant="info">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock size={20} />
            <div>
              <p className="font-semibold">
                {daysRemaining} days remaining in your trial
              </p>
              <p className="text-sm mt-1">
                You're doing great! Consider upgrading to continue after your trial ends.
              </p>
            </div>
          </div>
          <Link to="/settings">
            <Button variant="ghost" size="sm">
              View Plans
            </Button>
          </Link>
        </div>
      </Alert>
    );
  }

  return null;
};
